import { useMemo } from 'react';
import { NavLink } from '@tov-plus/frontend-data';
import { compareEventDisplayOrder, toGuestJourneyPath } from '@tov-plus/domain';
import { useTovQuery } from '@tov-plus/frontend-data';
import { guestApi } from '../app/api';
import { StatePanel } from '../components/StatePanel';
import { useGuestAccess } from '../contexts/GuestAccessContext';
import { guestQueryKeys } from '../lib/data/guestQueryKeys';
import { formatGuestDateTime } from '../lib/localization/guestLocale';
import { msg, TovCard, TovIcon } from '@tov-plus/ui';

type InviteProfileView = {
  rsvp: Awaited<ReturnType<typeof guestApi.getRsvp>>;
  drafts: Awaited<ReturnType<typeof guestApi.getDraftState>>;
  support: Awaited<ReturnType<typeof guestApi.getSupportInbox>>;
};
export function MyInvitePage() {
  const access = useGuestAccess();
  const readyOccasionSlug =
    access.status === 'ready' ? access.experience.occasion.slug : null;
  const readyGuestToken = access.status === 'ready' ? access.guestToken : null;
  const inviteProfileQuery = useTovQuery<InviteProfileView>({
    queryKey:
      readyOccasionSlug && readyGuestToken
        ? guestQueryKeys.inviteProfile(readyOccasionSlug, readyGuestToken)
        : guestQueryKeys.inviteProfile('pending', 'pending'),
    queryFn: async () => {
      const [rsvp, drafts, support] = await Promise.all([
        guestApi.getRsvp(readyOccasionSlug!, readyGuestToken!),
        guestApi.getDraftState(readyOccasionSlug!, readyGuestToken!),
        guestApi.getSupportInbox(readyOccasionSlug!, readyGuestToken!),
      ]);
      return { rsvp, drafts, support };
    },
    enabled: Boolean(readyOccasionSlug && readyGuestToken),
  });
  const view = inviteProfileQuery.data ?? null;
  const supportRequestCount = useMemo(() => {
    return view?.support.items.length ?? 0;
  }, [view]);
  const resolvedLocaleCode =
    access.status === 'ready'
      ? access.experience.localizationContext.resolvedLocaleCode
      : msg('pages.my_invite_page.en');
  const occasionTimezone =
    access.status === 'ready' ? access.experience.occasion.timezone : null;
  if (access.status !== 'ready' || inviteProfileQuery.isLoading) {
    return <StatePanel preset="loading" />;
  }
  if (inviteProfileQuery.isError || !view) {
    return <StatePanel preset="loading" />;
  }
  const pendingEvents = view.rsvp.events.filter(
    (event) => !event.response || event.response.status === 'pending',
  );
  const confirmedEvents = view.rsvp.events.filter(
    (event) => event.response?.status === 'attending',
  );
  const householdParticipants =
    access.experience.identitySummary.participantDisplayNames;
  const nextEvent =
    [...access.experience.events]
      .filter((event) => event.startsAt)
      .sort(compareEventDisplayOrder)[0] ?? null;
  const recommendedPath = toGuestJourneyPath(
    access.experience.journeySummary.recommendedTarget,
  );
  const activeFormDraftCount = Object.keys(view.drafts.formDrafts ?? {}).length;
  const householdResponseSummary = householdParticipants.map(
    (participantName) => ({
      participantName,
      pendingCount: pendingEvents.length,
      confirmedCount: confirmedEvents.length,
      formState:
        view.drafts.formDraftsUpdatedAt || view.drafts.formDrafts
          ? msg('pages.my_invite_page.drafts_in_progress')
          : msg('pages.my_invite_page.no_saved_form_draft'),
    }),
  );
  return (
    <div className="page-grid">
      <TovCard variant="content" isWide className="hero-card">
        <p className="page-eyebrow">{msg('app.navigation.my_invite')}</p>
        <h2>{view.rsvp.unitDisplayName}</h2>
        <p>
          {msg(
            'pages.my_invite_page.pages_my_invite_page_this_page_keeps_the_occasion_scoped_parts_of',
          )}{' '}
        </p>
        <div className="hero-card__stats">
          <article>
            <strong>{pendingEvents.length}</strong>
            <span>
              {msg(
                'pages.my_invite_page.pages_my_invite_page_rsvps_still_pending',
              )}
            </span>
          </article>
          <article>
            <strong>{confirmedEvents.length}</strong>
            <span>
              {msg(
                'pages.my_invite_page.pages_my_invite_page_confirmed_events',
              )}
            </span>
          </article>
          <article>
            <strong>{supportRequestCount}</strong>
            <span>
              {msg(
                'pages.my_invite_page.pages_my_invite_page_saved_support_requests',
              )}
            </span>
          </article>
          <article>
            <strong>{access.experience.accessLifecycle.status}</strong>
            <span>
              {msg(
                'pages.my_invite_page.pages_my_invite_page_invite_lifecycle',
              )}
            </span>
          </article>
        </div>
        <div className="action-row">
          <NavLink className="primary-button" to="/rsvp">
            {msg('app.guest_workflows.review_rsvp')}{' '}
          </NavLink>
          <NavLink className="secondary-link" to="/travel-packet">
            {msg(
              'pages.locations_page.pages_locations_page_open_travel_packet',
            )}{' '}
          </NavLink>
          <NavLink className="secondary-link" to="/help">
            {msg(
              'pages.my_invite_page.pages_my_invite_page_open_support_history',
            )}{' '}
          </NavLink>
          <NavLink className="secondary-link" to="/recent-invites">
            {msg(
              'pages.my_invite_page.pages_my_invite_page_returning_guest_view',
            )}{' '}
          </NavLink>
        </div>
      </TovCard>

      <TovCard variant="content" isWide>
        <p className="page-eyebrow">
          {msg('pages.my_invite_page.pages_my_invite_page_personal_snapshot')}
        </p>
        <div className="guest-invite-snapshot">
          <NavLink className="guest-invite-snapshot__item" to={recommendedPath}>
            <span>
              <TovIcon name="arrow-right" />
            </span>
            <strong>{access.experience.journeySummary.recommendedLabel}</strong>
            <p>{access.experience.journeySummary.summary}</p>
          </NavLink>
          <NavLink className="guest-invite-snapshot__item" to="/events">
            <span>
              <TovIcon name="calendar-blank" />
            </span>
            <strong>
              {nextEvent?.displayName ??
                msg('pages.my_invite_page.pages_my_invite_page_no_next_event')}
            </strong>
            <p>
              {nextEvent?.startsAt
                ? formatGuestDateTime(
                    nextEvent.startsAt,
                    resolvedLocaleCode,
                    nextEvent.timezone ?? occasionTimezone,
                  )
                : msg(
                    'pages.events_page.pages_events_page_time_not_published_yet',
                  )}
            </p>
          </NavLink>
          <NavLink className="guest-invite-snapshot__item" to="/forms">
            <span>
              <TovIcon name="file-text" />
            </span>
            <strong>
              {activeFormDraftCount}{' '}
              {msg('pages.my_invite_page.pages_my_invite_page_saved_draft')}
              {activeFormDraftCount === 1 ? '' : 's'}
            </strong>
            <p>
              {view.drafts.formDraftsUpdatedAt
                ? formatGuestDateTime(
                    view.drafts.formDraftsUpdatedAt,
                    resolvedLocaleCode,
                    occasionTimezone,
                  )
                : msg('pages.my_invite_page.no_saved_form_draft')}
            </p>
          </NavLink>
          <NavLink className="guest-invite-snapshot__item" to="/help">
            <span>
              <TovIcon name="lifebuoy" />
            </span>
            <strong>
              {supportRequestCount}{' '}
              {msg('pages.my_invite_page.pages_my_invite_page_support_request')}
              {supportRequestCount === 1 ? '' : 's'}
            </strong>
            <p>
              {supportRequestCount > 0
                ? msg(
                    'pages.my_invite_page.pages_my_invite_page_review_host_replies',
                  )
                : msg(
                    'pages.my_invite_page.pages_my_invite_page_no_open_support_items',
                  )}
            </p>
          </NavLink>
        </div>
      </TovCard>

      <TovCard variant="content">
        <p className="page-eyebrow">
          {msg('pages.access_page.pages_access_page_invite_scope')}
        </p>
        <div className="detail-list">
          <article>
            <strong>{msg('app.guest_workflows.access_mode')}</strong>
            <span>{access.experience.accessMode.replaceAll('_', ' ')}</span>
          </article>
          <article>
            <strong>
              {msg(
                'pages.my_invite_page.pages_my_invite_page_household_and_companions',
              )}
            </strong>
            <span>
              {access.experience.identitySummary.participantDisplayNames.join(
                ', ',
              ) ||
                msg(
                  'pages.my_invite_page.pages_my_invite_page_only_the_current_guest_unit_is_attached_right',
                )}
            </span>
          </article>
          <article>
            <strong>
              {msg('pages.my_invite_page.pages_my_invite_page_invite_status')}
            </strong>
            <span>{view.rsvp.invitation.status}</span>
          </article>
        </div>
      </TovCard>

      <TovCard variant="content">
        <p className="page-eyebrow">
          {msg('pages.my_invite_page.pages_my_invite_page_travel_continuity')}
        </p>
        <div className="detail-list">
          <article>
            <strong>
              {msg('pages.today_page.pages_today_page_travel_packet')}
            </strong>
            <span>
              {msg(
                'pages.my_invite_page.pages_my_invite_page_keep_a_publishing_mobile_friendly_travel_copy_ready',
              )}{' '}
            </span>
          </article>
          <article>
            <strong>
              {msg(
                'pages.access_page.pages_access_page_recommended_next_route',
              )}
            </strong>
            <span>{access.experience.journeySummary.recommendedLabel}</span>
          </article>
        </div>
      </TovCard>

      <TovCard variant="content" isWide>
        <p className="page-eyebrow">
          {msg(
            'pages.my_invite_page.pages_my_invite_page_household_management',
          )}
        </p>
        <div className="detail-list detail-list--stacked">
          {householdResponseSummary.length > 0 ? (
            householdResponseSummary.map((participant, index) => (
              <article
                key={`participant:${participant.participantName}:${index}`}
              >
                <strong>{participant.participantName}</strong>
                <span>
                  {msg(
                    'pages.my_invite_page.pages_my_invite_page_covered_by_the_same_invite_unit_and_shared',
                  )}{' '}
                </span>
                <span>
                  {msg(
                    'pages.my_invite_page.pages_my_invite_page_use_rsvp_and_forms_from_this_invite_to',
                  )}{' '}
                </span>
                <span>
                  {msg(
                    'pages.my_invite_page.pages_my_invite_page_pending_events',
                  )}{' '}
                  {participant.pendingCount}{' '}
                  {msg(
                    'pages.my_invite_page.pages_my_invite_page_confirmed_events',
                  )}{' '}
                  {participant.confirmedCount} • {participant.formState}
                </span>
              </article>
            ))
          ) : (
            <article>
              <strong>
                {msg(
                  'pages.my_invite_page.pages_my_invite_page_no_additional_household_participants',
                )}
              </strong>
              <span>
                {msg(
                  'pages.my_invite_page.pages_my_invite_page_this_invite_currently_resolves_to_an_individual_guest',
                )}{' '}
              </span>
            </article>
          )}
        </div>
      </TovCard>

      <TovCard variant="content" isWide>
        <p className="page-eyebrow">
          {msg('pages.my_invite_page.pages_my_invite_page_rsvp_summary')}
        </p>
        <div className="detail-list detail-list--stacked">
          {view.rsvp.events.map((event) => (
            <article key={event.eventId}>
              <strong>{event.displayName}</strong>
              <span>
                {event.response?.status ?? msg('general.status.pending')}
                {event.deadlineAt
                  ? ` • respond by ${formatGuestDateTime(event.deadlineAt, resolvedLocaleCode, occasionTimezone)}`
                  : ''}
              </span>
              <span>
                {event.response?.note ??
                  event.guestSummary ??
                  msg(
                    'pages.my_invite_page.pages_my_invite_page_no_guest_note_or_response_summary_is_saved',
                  )}
              </span>
            </article>
          ))}
        </div>
      </TovCard>

      <TovCard variant="content" isWide>
        <p className="page-eyebrow">
          {msg('pages.my_invite_page.pages_my_invite_page_support_history')}
        </p>
        {supportRequestCount > 0 ? (
          <div className="detail-list detail-list--stacked">
            {view.support.items.map((item) => (
              <article key={item.id}>
                <strong>{item.question}</strong>
                <span>
                  {formatGuestDateTime(
                    item.createdAt,
                    resolvedLocaleCode,
                    occasionTimezone,
                  )}{' '}
                  • {item.route}
                </span>
                <span>
                  {item.status.replaceAll('_', ' ')}
                  {item.priority ? ` • ${item.priority} priority` : ''}
                  {item.resolutionState
                    ? ` • ${item.resolutionState.replaceAll('_', ' ')}`
                    : ''}
                  {item.assignedOwner ? ` • owner ${item.assignedOwner}` : ''}
                </span>
                {item.acknowledgedAt ? (
                  <span>
                    {msg('pages.help_page.pages_help_page_acknowledged')}{' '}
                    {formatGuestDateTime(
                      item.acknowledgedAt,
                      resolvedLocaleCode,
                      occasionTimezone,
                    )}
                    {item.resolvedAt
                      ? ` • resolved ${formatGuestDateTime(item.resolvedAt, resolvedLocaleCode, occasionTimezone)}`
                      : ''}
                  </span>
                ) : null}
                {item.replies.length > 0 ? (
                  <span>
                    {msg(
                      'pages.my_invite_page.pages_my_invite_page_latest_planner_reply',
                    )}{' '}
                    {item.replies[item.replies.length - 1]?.body}
                  </span>
                ) : null}
              </article>
            ))}
          </div>
        ) : (
          <p>
            {msg(
              'pages.my_invite_page.pages_my_invite_page_no_support_requests_have_been_saved_from_this',
            )}
          </p>
        )}
      </TovCard>
    </div>
  );
}
